import {DateObject, DateTime} from "luxon";
import {TimeRange} from "../types/TimeRange";

const months: {[key: string]: string[]} = {
    'ru': ['Января', 'Февраля', 'Марта', 'Апреля', 'Мая', 'Июня', 'Июля', 'Августа', 'Сентября', 'Октября', 'Ноября', 'Декабря'],
	'uk': ['Січня', 'Лютого', 'Березня', 'Квітня', 'Травня', 'Червня', 'Липня', 'Серпня', 'Вересня', 'Жовтня', 'Листопада', 'Грудня'],
    'en': ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
	'zh': ['一月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '十二月']
}

const monthName = (monthNumber: number, locale = 'en'): string => {
	if (!months[locale]) {
		locale = 'en'
	}
    return months[locale][monthNumber - 1] || '';
}

/**
 * current and given offset difference
 * @param offset
 */
const offsetDiff = (offset: number) => {
	return (offset || 0) -  (DateTime.local().offset/60);
}

const zoneNameByOffset = (offset: number) => {
	if (offset == 0) {
		return 'UTC';
	}
	return 'UTC' + ((offset > 0) ? '+' : '') + offset.toString();
}

/**
 * Determine date time in the given offset, but if stayWithCurrentOffset DateTime instance wouldn't change its offset and just plus or minus offset
 * @param date
 * @param originalOffset
 * @param stayWithCurrentOffset
 */
const dateInOriginalOffset = (date: DateTime, originalOffset: number, stayWithCurrentOffset = false, keepLocalTime = false) => {
	if (stayWithCurrentOffset) {
		const offsetDifference = offsetDiff(originalOffset);
		return date.minus({
			hours: offsetDifference
		})
	}

	return date.setZone(zoneNameByOffset(originalOffset), { keepLocalTime: keepLocalTime });
}

/**
 * Determine how many days are needed to be added to current date (might be negative), if the given time is out of range (time range)
 * @param time
 * @param timeRange
 */
const incrementBasedOnRange = (time: string, timeRange: TimeRange) => {
		const parts = time.split(':'),
			hours = parts[0] ? parseInt(parts[0]) : null;
		if (hours != null) {
			if (24 + hours <= (timeRange.end as number)) {
				return 1
			}
			if (-1 * (24 - hours) >= (timeRange.begin as number)) {
				return -1;
			}
		}
		return 0;
}

const formatDate = (date: DateTime, lang = 'en', withoutYear = false) => {
	if (['ru', 'uk'].includes(lang)) {
		const monthNameStr = monthName(date.month, lang).toLowerCase();
		if (withoutYear) {
			return `${date.day} ${monthNameStr}`;
		}
		return `${date.day} ${monthNameStr} ${date.year}`;
	}
	return date.setLocale(lang).toFormat('LLLL d' + (withoutYear ? '' : ', yyyy'));
}

/**
 * Returns minimum limit date with offset
 * @param date
 * @param offset
 */
export const getMinLimitDate = (date: DateTime | undefined, offset: number) => {
	if (date) {
		return dateInOriginalOffset(date, offset).startOf("day");
	}
	return dateInOriginalOffset(DateTime.fromISO('1900-01-01'), offset).startOf('day');
}

/**
 * Returns maximum limit date with offset
 * @param date
 * @param offset
 */
export const getMaxLimitDate = (date: DateTime | undefined, offset: number) => {
	if (date) {
		return dateInOriginalOffset(date, offset).endOf('day');
	}
	return dateInOriginalOffset(DateTime.fromISO('2900-01-01'), offset);
}

/**
 * Determines if two dates are the same day
 * @param date
 * @param comparableDate
 */
const isSameDate = (date: DateTime, comparableDate: DateTime) => {
	return date.hasSame(comparableDate, 'year') && date.hasSame(comparableDate, 'month') && date.hasSame(comparableDate, 'day');
}

// возвращает форматированную строку с датами проведения сессии/мероприятия для вывода в карточке
const formatIntervalDate = (
	beginDateObject: DateObject,
	endDateObject: DateObject | undefined | null,
	lang = 'en',
	sameDayOnlyDate = false, // возвращать ли только дату (без времени), если начало и конец относятся к одному дню,
	dayFirst = false
) => {
	const beginDay = beginDateObject?.day;
	const beginMonth = beginDateObject.month;
	const beginYear = beginDateObject?.year;
	const currentYear = DateTime.local().year;

	if (!['en', 'ru', 'uk', 'zh'].includes(lang)) {
		lang = 'en';
	}

	if (beginMonth) {

		const beginMonthName = monthName(beginMonth, lang).toLowerCase();

		if (!endDateObject) {
			//no date end
			return `${beginDay} ${beginMonthName} ${beginYear !== currentYear ? beginYear : ''}`.trim();
		}

		const endDay = endDateObject?.day;
		const endMonth = endDateObject?.month;
		const endYear = endDateObject?.year;

		if (endMonth) {

			const endMonthName = monthName(endMonth, lang).toLowerCase();

			//1 декабря 2020–3 января 2021
			if (beginYear !== endYear) {
				return `${beginDay} ${beginMonthName} ${beginYear} – ${endDay} ${endMonthName} ${endYear}`
			}

			//15 января-3 февряля 2021
			if (beginMonth !== endMonth) {
				return `${beginDay} ${beginMonthName} – ${endDay} ${endMonthName} ${endYear !== currentYear ? endYear : ''}`.trim();
			}

			//same day
			//09:58 - 17:00, 12 марта
			if (beginDay === endDay) {
				const begin = DateTime.fromObject(beginDateObject);
				const end = DateTime.fromObject(endDateObject);

				if (sameDayOnlyDate) {
					return `${begin.toFormat("d")} ${beginMonthName}`
				}
				else {
					return dayFirst
						? `${begin.toFormat("d")} ${beginMonthName}, ${begin.toFormat("T")} - ${end.toFormat("T")}`
						: `${begin.toFormat("T")} - ${end.toFormat("T")}, ${begin.toFormat("d")} ${beginMonthName}`
				}
			}

			//15-18 февряля 2021 || 15-18 февряля
			return `${beginDay}–${endDay} ${endMonthName} ${endYear !== currentYear ? endYear : ''}`.trim();
		}
	}

	return "";
}

const getDatesFormatedString = (dates: string[] | null, offset: number, lang: string) => {
	const result: { date: DateTime; formattedDate: string }[] = [];
	const today = dateInOriginalOffset(DateTime.local(), offset);

	if (Array.isArray(dates)) {
		dates.forEach(date => {
			const dateInstance = DateTime.fromFormat(date, "d.MM.yyyy", { zone: zoneNameByOffset(offset) });
			result.push({
				date: dateInstance,
				formattedDate: formatDate(dateInstance, lang, dateInstance.hasSame(today, 'year'))
			})
		})
	}
	return result;
}

/**
 *
 * @param min - count hours from min value
 * @param max - count hours to max value
 * @param interval - interaval for minutes
 * @returns list of string with format ['00:00', '00:05', ...]
 */
const getTimeList = (min = 0, max = 24, interval = 60) => {
	const result: string[] = [];

	const getPair = (hour: number, minutes: number) => {
		return {
			hour: hour < 10 ? `0${hour}` : hour,
			minutes: minutes < 10 ? `0${minutes}` : minutes
		}
	}

	for (let i = min; i < max; i++) {
		for (let j = 0; j <= 60 - interval; j += interval) {
			const {
				hour, minutes
			} = getPair(i, j);

			const time = `${hour}:${minutes}`;
			result.push(time);
		}
	}

	return result
}

/**
 * converts string to DateTime
 * @param date
 * @returns
 */
const convertToDateTime = (date: DateTime | string | undefined | null) => {
	if(date instanceof DateTime) {
		return date;
	}

	if(typeof date !== "string" && !date) {
		return date;
	}

	if (typeof date === "string" && date) {
		return DateTime.fromISO(date)
	}
}

const dateToFormatFromIso = (value: string, format: string): string => {
	try {
		return DateTime.fromISO(value).toFormat(format);
	} catch (e) {
		return '';
	}
}

const dateFromFormatToIso = (value: string, format: string): string | null => {
	try {
		return value ? DateTime.fromFormat(value, format)?.toISO() : null;
	} catch (e) {
		return '';
	}
}

const getDateByTimeZoneOffset = (date: DateTime, timeZoneOffset: number | null) => {
	return timeZoneOffset || timeZoneOffset === 0 ? dateInOriginalOffset(date, timeZoneOffset) : date;
}

export {
	convertToDateTime,
	monthName,
	isSameDate,
	dateInOriginalOffset,
	offsetDiff,
	incrementBasedOnRange,
	formatDate,
	formatIntervalDate,
	zoneNameByOffset,
	getDatesFormatedString,
	getTimeList,
	dateToFormatFromIso,
	dateFromFormatToIso,
	getDateByTimeZoneOffset
}
